/**
 * FormsDslRuntime — renders + drives a compiled DSL form Plan (Phase 2c).
 *
 * Builds a reactive value per binding, evaluates the serialized expression ASTs
 * (visible/enabled/default/computed) via formsdsl-eval, fetches select/table data
 * through the query endpoint (re-running when binds change), and fires a button's
 * resolved run{} steps through the action endpoint (require-veto + dry-run aware).
 *
 * Props: plan, connectionId, database, onClose?
 * Scope value model (matches formsdsl-eval): a select/table value is its picked
 * ROW object; scalar inputs are primitives. SQL binds use the value column.
 */
import { createSignal, createMemo, createResource, createEffect, on, onMount, onCleanup, For, Show, Switch, Match } from 'solid-js';
import { createStore } from 'solid-js/store';
import { evalExpr } from '../lib/formsdsl-eval.js';
import { changeset, resolveSteps } from '../lib/formsdsl-grid.js';
import { runQuery, runAction, runFormQuery, runFormAction } from '../lib/formsdsl-api.js';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { applyFormat } from '../lib/format.js';
import { toast } from '../lib/toast.js';

// label/note `size` keyword scale (mirrors meta.width's sm/md/lg/xl + raw-value
// fallback pattern — see formStyle() below). A value not in this map is used as a
// raw CSS font-size (e.g. "20px", "1.4em").
const FONT_SIZE_SCALE = { sm: '11px', md: '13px', lg: '16px', xl: '20px' };

export default function FormsDslRuntime(props) {
  const [values, setValues] = createStore({});
  const [refreshTick, setRefreshTick] = createSignal(0);
  // Bumped when the connection/database changes; query widgets watch it to reset
  // their component-local UI state (search box, open dropdown) — see the effect below.
  const [resetTick, setResetTick] = createSignal(0);

  // Active connection/database: a `connection`/`database` picker in the form
  // overrides the props (which seed the default, e.g. from the workbench picker).
  const [pickConn, setPickConn] = createSignal(null);
  const [pickDb, setPickDb] = createSignal(null);
  const [pickConnRaw, setPickConnRaw] = createSignal(null); // the chosen target's connection string (for run-by-id routing)
  // byId = a SAVED form run (Forms tab); else the admin workbench preview.
  const byId = () => props.formId !== undefined && props.formId !== null && props.formId !== '';
  // A SAVED form with a `target` widget routes through it: until a target is picked there
  // is no connection (so nothing queries the form's default). In the workbench preview,
  // and for forms without a target, the props (form default / toolbar picker) apply.
  const hasTarget = () => {
    const find = (nodes) => (nodes || []).some((n) => n.kind === 'target' || find(n.children));
    return find(props.plan?.nodes);
  };
  const connId = () => pickConn() ?? (byId() && hasTarget() ? null : props.connectionId);
  const database = () => pickDb() ?? (byId() && hasTarget() ? null : props.database);

  // Two run modes. Workbench preview (admin): connection chosen here, raw SQL sent to
  // the connection-scoped endpoints. Saved-form run (props.formId set): the server
  // drives everything from the STORED script via the grant-checked run-by-id endpoints
  // — the client sends the node name / chosen steps + values + the chosen target (the
  // server validates it against the form's `target` options), never raw SQL.
  const doQuery = (n, key, extra) => (byId()
    ? runFormQuery(props.formId, { node: n.name, binds: key.binds, terms: key.terms, connection: pickConnRaw() || '', database: database() || '', ...extra })
    : runQuery(connId(), { database: database(), query: n.query.raw, binds: key.binds, terms: key.terms, ...extra }));
  const doAction = (steps, dryRun) => (byId()
    ? runFormAction(props.formId, { dryRun, steps, connection: pickConnRaw() || '', database: database() || '' })
    : runAction(connId(), { database: database(), dryRun, steps }));

  // Connection list (for a `connection` picker) + databases on the active conn.
  const [connList] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [dbList] = createResource(connId, async (id) => {
    if (!id) return [];
    try { return (await api.get(`/api/connections/${id}/databases`)).databases || []; } catch { return []; }
  });

  // value-column map (binding → its value column), for scalar extraction. A memo so
  // it rebuilds when the plan changes on re-analyze (no stale [object Object]).
  const valueCol = createMemo(() => {
    const m = {};
    const walk = (nodes) => {
      for (const n of nodes || []) {
        if (n.node === 'widget' && n.value) m[n.name] = n.value;
        if (n.children) walk(n.children);
      }
    };
    walk(props.plan?.nodes);
    return m;
  });

  const userCtx = () => {
    const u = currentUser();
    return u ? { id: u.id, name: u.username, role: u.role } : {};
  };

  // rawBind resolves a query's :bind path from WIDGET values only (no lets) — so async
  // query lets can key/run without a reactive cycle through letVals.
  const rawBind = (path) => {
    const dot = path.indexOf('.');
    if (dot >= 0) {
      const root = path.slice(0, dot), field = path.slice(dot + 1);
      const v = values[root];
      return v == null ? null : v[field];
    }
    const v = values[path];
    const vc = valueCol()[path];
    if (vc && v && typeof v === 'object') return v[vc];
    return v;
  };

  // Async query lets: `let x = sql.scalar/row/rows("…")`. One reactive resource runs
  // them all (re-running when their binds / connection / refreshTick change) and
  // returns { name: value }: scalar→first cell, row→first row object, rows→[objects].
  const asyncLets = createMemo(() => (props.plan?.lets || []).filter((l) => l.query));
  const asyncKey = createMemo(() => {
    const al = asyncLets();
    if (!al.length || (!byId() && !connId())) return null;
    const k = { c: connId(), d: database(), r: refreshTick(), b: {} };
    for (const l of al) {
      const b = {}; for (const p of l.query.binds || []) b[p] = rawBind(p);
      k.b[l.name] = b;
    }
    return JSON.stringify(k);
  });
  const shapeLet = (r, kind) => {
    const cols = r?.columns || [], rows = r?.rows || [];
    if (kind === 'scalar') return rows.length ? rows[0][0] : null;
    const obj = (row) => Object.fromEntries(cols.map((c, i) => [c, row[i]]));
    if (kind === 'row') return rows.length ? obj(rows[0]) : null;
    return rows.map(obj);
  };
  const [asyncVals] = createResource(asyncKey, async () => {
    const out = {};
    await Promise.all(asyncLets().map(async (l) => {
      try {
        const binds = {}; for (const p of l.query.binds || []) binds[p] = rawBind(p);
        const r = await doQuery({ name: l.name, query: l.query }, { binds, terms: {} }, { limit: l.kind === 'rows' ? 1000 : 1 });
        out[l.name] = shapeLet(r, l.kind);
      } catch { out[l.name] = null; }
    }));
    return out;
  });

  // `let` values, in recompute order: expression lets via evalExpr against the
  // accumulating scope; query lets from the async resource above (null while loading).
  // It re-evaluates on any value change (the scope spreads every value), so it only
  // NOTIFIES when a let actually changed — else every reader of a let (a query's
  // bind, an unpicked select's fallback) would re-run on each keystroke anywhere.
  const sameLets = (a, b) => {
    if (!a || !b) return a === b;
    const ka = Object.keys(a);
    return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
  };
  const letVals = createMemo(() => {
    const sc = { ...values, user: userCtx() };
    const out = {};
    const av = asyncVals();
    for (const l of props.plan?.lets || []) {
      let v;
      if (l.query) v = av ? (av[l.name] ?? null) : null;
      else { try { v = evalExpr(l.expr, sc); } catch { v = null; } }
      out[l.name] = v ?? null;
      sc[l.name] = out[l.name];
    }
    return out;
  }, undefined, { equals: sameLets });

  const scope = () => ({ ...values, user: userCtx(), ...letVals() });
  // scope()[name] without building scope(): reads (and so tracks) only that name.
  const scopeValue = (name) => {
    const lv = letVals();
    if (Object.prototype.hasOwnProperty.call(lv, name)) return lv[name];
    return name === 'user' ? userCtx() : values[name];
  };

  const scalarValue = (name) => {
    const v = values[name] !== undefined ? values[name] : letVals()[name];
    const vc = valueCol()[name];
    if (vc && v && typeof v === 'object') return v[vc];
    return v;
  };
  const bindValue = (path) => {
    const dot = path.indexOf('.');
    if (dot >= 0) {
      const root = path.slice(0, dot), field = path.slice(dot + 1);
      const v = scopeValue(root);
      return v == null ? null : v[field];
    }
    return scalarValue(path);
  };
  const evalProp = (node, key, dflt) => {
    const e = node.exprs?.[key];
    if (e === undefined) return dflt;
    try { return evalExpr(e, scope()); } catch { return dflt; }
  };
  // Display interpolation: {name} → scalar value (a table/select's value column, or a
  // let); {root.field} → a nested field, e.g. {results.TglJual} or {user.name}.
  const interp = (tpl) => String(tpl || '').replace(/\{([\w.]+)\}/g, (_, path) => {
    const v = path.indexOf('.') >= 0 ? bindValue(path) : scalarValue(path);
    return v == null ? '' : String(v);
  });
  const safeEval = (ast, sc) => { try { return evalExpr(ast, sc); } catch { return null; } };
  // Resolve a bind path against an optional `extra` scope (a for-loop's row var)
  // first, else the form scope. `interpWith` is the {interp} variant.
  const bindWith = (path, extra) => {
    if (extra) {
      const dot = path.indexOf('.');
      const root = dot >= 0 ? path.slice(0, dot) : path;
      if (Object.prototype.hasOwnProperty.call(extra, root)) {
        if (dot < 0) return extra[root];
        const v = extra[root];
        return v == null ? null : v[path.slice(dot + 1)];
      }
    }
    return bindValue(path);
  };
  const interpWith = (tpl, extra) => String(tpl || '').replace(/\{([\w.]+)\}/g, (_, path) => {
    const v = bindWith(path, extra);
    return v == null ? '' : String(v);
  });
  const fmtCell = (v) => (v == null ? '' : String(v));
  // Display a value through an optional `format` mask (numbers/dates).
  const fmtVal = (v, mask) => (mask ? applyFormat(v, mask) : fmtCell(v));

  // ── Input constraints (required / min / max / maxlen / pattern) ─────────────
  // The grammar accepts these on widgets + columns; we enforce them before a
  // mutating action (the browser attributes below are just live hints). Returns
  // the first violation message for a single field, or null.
  const reCache = {};
  const reTest = (pat, s) => { try { return (reCache[pat] ||= new RegExp(pat)).test(s); } catch { return true; } };
  const fieldLabel = (n) => n.label || n.name;
  const constraintError = (s, kind, value, label) => {
    const empty = value == null || value === '';
    if (s?.required && empty) return `${label} is required.`;
    if (empty || !s) return null; // no scalars → no min/max/maxlen/pattern to check
    if (kind === 'number') {
      const num = Number(value);
      if (s.min != null && Number.isFinite(num) && num < Number(s.min)) return `${label} must be at least ${s.min}.`;
      if (s.max != null && Number.isFinite(num) && num > Number(s.max)) return `${label} must be at most ${s.max}.`;
    }
    if (kind === 'text' || kind === undefined) {
      if (s.maxlen != null && String(value).length > Number(s.maxlen)) return `${label} must be ${s.maxlen} characters or fewer.`;
      if (s.pattern && !reTest(s.pattern, String(value))) return `${label} is not in the expected format.`;
    }
    return null;
  };
  // First constraint violation across visible scalar/select/table widgets, or null.
  const firstFieldError = () => {
    let err = null;
    const walk = (nodes) => {
      for (const n of nodes || []) {
        if (err) return;
        if (n.node === 'widget' && ['text', 'number', 'date', 'check', 'select', 'table'].includes(n.kind) && evalProp(n, 'visible', true)) {
          const e = constraintError(n.scalars, n.kind, scalarValue(n.name), fieldLabel(n));
          if (e) { err = e; return; }
        }
        if (n.children) walk(n.children);
      }
    };
    walk(props.plan?.nodes);
    return err;
  };

  // meta.width → a centered max-width on the form (sm/md/lg/xl/full, or a raw px/em).
  const formStyle = () => {
    const w = { sm: '460px', md: '680px', lg: '920px', xl: '1160px', full: 'none' }[props.plan?.meta?.width]
      || (props.plan?.meta?.width || '');
    if (!w || w === 'none') return undefined;
    return { 'max-width': w, margin: '0 auto', width: '100%' };
  };

  // Seed `param` bindings from the opening context (props.params, e.g. passed by
  // another form's `open … with { }`) + their declared defaults, so queries and
  // expressions that reference a param resolve from the first render.
  for (const pm of props.plan?.params || []) {
    if (values[pm.name] !== undefined) continue;
    let v = props.params?.[pm.name];
    if (v === undefined) { try { v = pm.default != null ? evalExpr(pm.default, { user: userCtx() }) : null; } catch { v = null; } }
    setValues(pm.name, v ?? null);
  }

  // Clear stale selections when the active connection/database changes. A select/table's
  // picked row is a snapshot of the OLD connection's data; keeping it would let a button
  // run (e.g. a delete) against the NEW connection with the OLD branch's key. Query
  // widgets re-fetch automatically (their depKey tracks connId/database) — here we just
  // drop the now-stale selection so dependent `enabled`/labels reset until the user
  // re-picks. `on(..., {defer:true})` tracks ONLY connId/database (not every value read
  // in the body) and skips the initial mount run. Grids self-reset via their own
  // data-driven effect, so they're left out.
  createEffect(on(() => [connId(), database()], () => {
    const walk = (nodes) => {
      for (const n of nodes || []) {
        if (n.node === 'widget' && (n.kind === 'select' || n.kind === 'table') && values[n.name] != null) {
          setValues(n.name, null);
        }
        if (n.children) walk(n.children);
      }
    };
    walk(props.plan?.nodes);
    setResetTick((t) => t + 1); // reset query widgets' search boxes + close open dropdowns
  }, { defer: true }));

  return (
    <div class="fdsl-form" style={formStyle()}>
      <Show when={props.plan?.diagnostics?.length}>
        <div class="fdsl-diags">
          <For each={props.plan.diagnostics}>{(d) => <div class="fdsl-diag">{d.code} — {d.msg} <span class="muted">({d.line}:{d.col})</span></div>}</For>
        </div>
      </Show>
      <For each={props.plan?.nodes}>{(n) => <RenderNode n={n} />}</For>
    </div>
  );

  function RenderNode(p) {
    const visible = createMemo(() => evalProp(p.n, 'visible', true));
    return <Show when={visible()}>{body(p.n)}</Show>;
  }

  function body(n) {
    switch (n.node) {
      case 'section':
        return <fieldset class="fdsl-section"><legend>{n.label}</legend><For each={n.children}>{(c) => <RenderNode n={c} />}</For></fieldset>;
      case 'row':
        return <div class="fdsl-row"><For each={n.children}>{(c) => <RenderNode n={c} />}</For></div>;
      case 'button':
        return <ButtonNode n={n} />;
      case 'widget':
        return <WidgetNode n={n} />;
    }
    return null;
  }

  function WidgetNode(p) {
    const n = p.n;
    const enabled = createMemo(() => evalProp(n, 'enabled', true));

    if (['text', 'number', 'date', 'check'].includes(n.kind)) {
      if (values[n.name] === undefined) {
        const d = n.exprs?.default !== undefined ? evalProp(n, 'default', null) : null;
        setValues(n.name, d ?? (n.kind === 'check' ? false : ''));
      }
      const ro = !!n.scalars?.readonly;
      return (
        <label class="fdsl-field">
          <span class="fdsl-label">{n.label || n.name}</span>
          <Switch>
            <Match when={n.kind === 'check'}>
              <input type="checkbox" disabled={ro || !enabled()} checked={!!values[n.name]}
                     onChange={(e) => setValues(n.name, e.currentTarget.checked)} />
            </Match>
            <Match when={true}>
              <input class="input" type={n.kind === 'number' ? 'number' : n.kind === 'date' ? 'date' : 'text'}
                     readonly={ro} disabled={!enabled()}
                     min={n.kind === 'number' ? n.scalars?.min : undefined}
                     max={n.kind === 'number' ? n.scalars?.max : undefined}
                     maxlength={n.kind === 'text' ? n.scalars?.maxlen : undefined}
                     pattern={n.kind === 'text' ? n.scalars?.pattern : undefined}
                     value={ro && n.scalars?.format ? applyFormat(values[n.name], n.scalars.format) : (values[n.name] ?? '')}
                     onInput={(e) => setValues(n.name, e.currentTarget.value)} />
            </Match>
          </Switch>
        </label>
      );
    }
    if (n.kind === 'select' || n.kind === 'table') return <QueryWidget n={n} enabled={enabled} />;
    if (n.kind === 'connection') {
      return (
        <label class="fdsl-field">
          <span class="fdsl-label">{n.label || 'Connection'}</span>
          <select class="input" onChange={(e) => { const id = e.currentTarget.value; setPickConn(id || null); setPickDb(null); setValues(n.name, id); }}>
            <option value="">— connection —</option>
            <For each={connList() || []}>{(c) => <option value={c.id} selected={String(c.id) === String(connId())}>{c.name}</option>}</For>
          </select>
        </label>
      );
    }
    if (n.kind === 'database') {
      return (
        <label class="fdsl-field">
          <span class="fdsl-label">{n.label || 'Database'}</span>
          <select class="input" disabled={!connId()} onChange={(e) => { const db = e.currentTarget.value; setPickDb(db || null); setValues(n.name, db); }}>
            <option value="">{dbList.loading ? 'Loading…' : '— database —'}</option>
            <For each={dbList() || []}>{(d) => <option value={d.name ?? d} selected={(d.name ?? d) === database()}>{d.name ?? d}</option>}</For>
          </select>
        </label>
      );
    }
    if (n.kind === 'target') {
      // Preset (connection, database) pairs shown under a friendly alias. Picking
      // one routes the form to the right connection+database.
      const resolveConn = (name) => {
        const c = (connList() || []).find((x) => x.name === name || String(x.id) === String(name));
        return c ? c.id : name;
      };
      return (
        <label class="fdsl-field">
          <span class="fdsl-label">{n.label || 'Target'}</span>
          <select class="input" onChange={(e) => {
            const i = e.currentTarget.selectedIndex - 1;
            const opt = i >= 0 ? n.options[i] : null;
            if (opt) { setPickConn(resolveConn(opt.connection)); setPickConnRaw(opt.connection); setPickDb(opt.database || null); setValues(n.name, opt.label); }
            else { setPickConn(null); setPickConnRaw(null); setPickDb(null); setValues(n.name, null); }
          }}>
            <option value="">— {n.label || 'pilih'} —</option>
            <For each={n.options || []}>{(o) => <option>{o.label}</option>}</For>
          </select>
        </label>
      );
    }
    if (n.kind === 'label' || n.kind === 'note') {
      const style = {};
      if (n.scalars?.bold) style['font-weight'] = 'bold';
      if (n.scalars?.italic) style['font-style'] = 'italic';
      if (n.scalars?.size) style['font-size'] = FONT_SIZE_SCALE[n.scalars.size] || n.scalars.size;
      return <div class="fdsl-note" style={style}>{interp(n.label)}</div>;
    }
    if (n.kind === 'spacer') {
      const horizontal = n.scalars?.direction === 'horizontal';
      const size = n.scalars?.size || '1rem';
      return horizontal
        ? <div class="fdsl-spacer fdsl-spacer-h" style={{ width: size }} />
        : <div class="fdsl-spacer fdsl-spacer-v" style={{ height: size }} />;
    }
    if (n.kind === 'grid') return <GridWidget n={n} />;
    return null;
  }

  function GridWidget(p) {
    const n = p.n;
    const cols = () => n.children || [];
    const key = n.key;
    const userC = userCtx;

    // Lookup columns: `column x { lookup = "SELECT val, label FROM …" }` renders a
    // dropdown from that query. One shared resource per lookup column (re-runs on
    // conn/db or its :binds changing). value = the column's `value` or the 1st
    // result column; label = `map` / `display` / the 2nd column.
    const lookups = {};
    for (const c of (n.children || [])) {
      if (c.scalars?.lookup && c.query?.raw) {
        const dk = createMemo(() => {
          const binds = {}; for (const path of c.query.binds || []) binds[path] = bindValue(path);
          return JSON.stringify({ binds, rt: refreshTick(), cid: connId(), db: database() });
        });
        const [d] = createResource(dk, async (k) => {
          try { return await doQuery(c, JSON.parse(k), { limit: 1000 }); } catch { return { columns: [], rows: [] }; }
        });
        lookups[c.name] = d;
      }
    }
    const lookupOptions = (col) => {
      const d = lookups[col.name]?.(); if (!d) return [];
      const cc = d.columns || [];
      const valCol = col.value || cc[0];
      const labCol = col.scalars?.map || col.display || cc[1] || valCol;
      return (d.rows || []).map((r) => { const o = {}; cc.forEach((c2, i) => { o[c2] = r[i]; }); return { value: o[valCol], label: o[labCol] }; });
    };
    const lookupLabel = (col, val) => {
      if (val == null || val === '') return '';
      const o = lookupOptions(col).find((x) => String(x.value) === String(val));
      return o ? o.label : val;
    };

    // A serialized key: the resource refetches (and the effect below resets the
    // working rows) only when a bind/term actually changed — not whenever the memo
    // re-runs and builds an equal-but-new object.
    const depKey = createMemo(() => {
      const binds = {}; for (const path of n.query?.binds || []) binds[path] = bindValue(path);
      const terms = {}; for (const t of n.query?.search || []) terms[t] = scalarValue(t);
      return JSON.stringify({ binds, terms, rt: refreshTick(), cid: connId(), db: database() });
    });
    const [data] = createResource(depKey, async (k) => {
      if (!n.query?.raw) return { columns: [], rows: [] };
      try {
        return await doQuery(n, JSON.parse(k), { limit: 1000 });
      } catch (e) { toast.error('Grid query failed: ' + (e?.message || e)); return { columns: [], rows: [] }; }
    });
    const originals = () => {
      const c = data()?.columns || [];
      return (data()?.rows || []).map((r) => Object.fromEntries(c.map((cc, i) => [cc, r[i]])));
    };

    const [working, setWorking] = createStore([]);
    const [removed, setRemoved] = createSignal([]);
    createEffect(() => {
      const o = originals();
      setWorking(o.map((r) => ({ __k: r[key], ...r })));
      setRemoved([]);
    });

    // When the master binds change (a different parent row picked, or it's cleared),
    // drop any unsaved edits/deletions *immediately* so a previous master's pending
    // changes never carry over while the re-query is in flight. The effect above
    // repopulates `working` once the new rows land.
    const masterKey = createMemo(() => {
      const b = {}; for (const path of n.query?.binds || []) b[path] = bindValue(path);
      return JSON.stringify(b);
    });
    createEffect(on(masterKey, () => { setWorking([]); setRemoved([]); }, { defer: true }));

    // Per-column text alignment (e.g. `column Total { align = right }`).
    const alignStyle = (col) => {
      const a = col.scalars?.align;
      return (a === 'right' || a === 'center' || a === 'left') ? { 'text-align': a } : undefined;
    };
    // Whether the grid has any unsaved change — drives the Save button's enabled state.
    const dirty = createMemo(() => {
      const cs = changeset({ originals: originals(), working: working.map((r) => ({ ...r })), removed: removed(), keyCol: key });
      return cs.added.length + cs.edited.length + cs.removed.length > 0;
    });

    const dataCols = () => cols().filter((c) => !c.exprs?.computed);
    const rowScope = (row) => ({ ...values, user: userC(), ...row });
    const cellRO = (col, row) => {
      const e = col.scalars?.edit;
      if (col.exprs?.computed || col.scalars?.readonly || e === 'never') return true;
      if (e === 'insert' && row.__k != null) return true;
      return false;
    };
    const addRow = () => { const blank = { __k: null }; for (const c of dataCols()) blank[c.name] = ''; setWorking(working.length, blank); };
    const removeRow = (i) => {
      const row = working[i];
      if (row.__k != null) setRemoved([...removed(), row.__k]);
      setWorking((w) => w.filter((_, j) => j !== i));
    };
    const footerVal = (col) => {
      let s = 0;
      for (const r of working) {
        const v = col.exprs?.computed ? safeEval(col.exprs.computed, rowScope(r)) : r[col.name];
        const nn = Number(v);
        if (!Number.isNaN(nn)) s += nn;
      }
      return s;
    };
    const hasFooter = () => cols().some((c) => c.scalars?.footer === 'sum');

    // Publish this grid's live rows (incl. computed columns) + changeset buckets
    // into the form scope under its name, so expressions (`sum(g.rows.total)`) and
    // run-block `for x in g.rows` loops can read them. Recomputes as the user edits.
    createEffect(() => {
      const plain = working.map((r) => {
        const o = {};
        for (const k in r) if (k !== '__k') o[k] = r[k];
        for (const c of cols()) if (c.exprs?.computed) o[c.name] = safeEval(c.exprs.computed, rowScope(r));
        return o;
      });
      const cs = changeset({ originals: originals(), working: working.map((r) => ({ ...r })), removed: removed(), keyCol: key });
      setValues(n.name, { rows: plain, added: cs.added, edited: cs.edited, removed: cs.removed });
    });

    // Constraint check for an about-to-be-written row → first violation or null.
    const rowError = (rowObj) => {
      for (const c of cols()) {
        if (c.exprs?.computed) continue;
        const e = constraintError(c.scalars, c.scalars?.type, rowObj[c.name], c.label || c.name);
        if (e) return e;
      }
      return null;
    };

    // In flight, and until the post-save refetch lands (the rows still diff as
    // dirty until then), a second click would send the same inserts again.
    const [saving, setSaving] = createSignal(false);
    const save = async () => {
      if (saving() || data.loading) return;
      const cs = changeset({ originals: originals(), working: working.map((r) => ({ ...r })), removed: removed(), keyCol: key });
      for (const r of [...cs.added, ...cs.edited]) { const e = rowError(r); if (e) { toast.error(`${n.label || n.name}: ${e}`); return; } }
      const steps = resolveSteps(n.persist, cs, (path) => bindValue(path));
      if (!steps.length) { toast.success('Nothing to save'); return; }
      setSaving(true);
      try {
        const res = await doAction(steps, false);
        if (res.aborted) { toast.error(res.message || 'Save blocked.'); return; }
        toast.success(`Saved (${res.rowsAffected} row${res.rowsAffected === 1 ? '' : 's'})`);
        setRefreshTick((t) => t + 1);
      } catch (e) { toast.error('Save failed: ' + (e?.message || e)); }
      finally { setSaving(false); }
    };

    const cell = (col, row, i) => {
      if (col.exprs?.computed) return <span class="fdsl-computed">{fmtVal(safeEval(col.exprs.computed, rowScope(row)), col.scalars?.format)}</span>;
      if (col.scalars?.lookup) {
        if (cellRO(col, row)) return <span>{fmtCell(lookupLabel(col, row[col.name]))}</span>;
        return (
          <select class="input fdsl-cell" value={row[col.name] ?? ''} onChange={(e) => setWorking(i, col.name, e.currentTarget.value)}>
            <option value="" />
            <For each={lookupOptions(col)}>{(o) => <option value={o.value}>{o.label}</option>}</For>
          </select>
        );
      }
      if (cellRO(col, row)) return <span>{fmtVal(row[col.name], col.scalars?.format)}</span>;
      const type = col.scalars?.type;
      if (type === 'select' && col.options?.length) {
        return (
          <select class="input fdsl-cell" value={row[col.name] ?? ''} onChange={(e) => setWorking(i, col.name, e.currentTarget.value)}>
            <option value="" />
            <For each={col.options}>{(o) => <option value={o.value}>{o.label}</option>}</For>
          </select>
        );
      }
      if (type === 'check') return <input type="checkbox" checked={!!row[col.name]} onChange={(e) => setWorking(i, col.name, e.currentTarget.checked)} />;
      return <input class="input fdsl-cell" type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
                    style={alignStyle(col)}
                    min={type === 'number' ? col.scalars?.min : undefined} max={type === 'number' ? col.scalars?.max : undefined}
                    maxlength={col.scalars?.maxlen} pattern={col.scalars?.pattern}
                    value={row[col.name] ?? ''} onInput={(e) => setWorking(i, col.name, e.currentTarget.value)} />;
    };

    return (
      <div class="fdsl-tablewrap">
        <div class="fdsl-label">{n.label || n.name}</div>
        <div class="fdsl-grid">
          <table>
            <thead><tr>
              <For each={cols()}>{(c) => <th style={alignStyle(c)}>{c.label || c.name}</th>}</For>
              <Show when={n.scalars?.remove}><th /></Show>
            </tr></thead>
            <tbody>
              <For each={working}>{(row, i) => (
                <tr>
                  <For each={cols()}>{(c) => <td style={alignStyle(c)}>{cell(c, row, i())}</td>}</For>
                  <Show when={n.scalars?.remove}><td><button class="btn btn-sm btn-ghost" onClick={() => removeRow(i())}>✕</button></td></Show>
                </tr>
              )}</For>
              <Show when={working.length === 0}><tr><td colspan={cols().length + 1} class="muted">no rows</td></tr></Show>
            </tbody>
            <Show when={hasFooter()}>
              <tfoot><tr>
                <For each={cols()}>{(c) => <td class="fdsl-foot" style={alignStyle(c)}>{c.scalars?.footer === 'sum' ? fmtVal(footerVal(c), c.scalars?.format) : ''}</td>}</For>
                <Show when={n.scalars?.remove}><td /></Show>
              </tr></tfoot>
            </Show>
          </table>
        </div>
        <div class="fdsl-gridbar">
          <Show when={n.scalars?.add}><button class="btn btn-sm btn-ghost" onClick={addRow}>＋ Row</button></Show>
          <Show when={n.persist}><button class="btn btn-sm btn-primary" disabled={!dirty() || saving() || data.loading} onClick={save}>{n.scalars?.save || '💾 Save'}</button></Show>
        </div>
      </div>
    );
  }

  function QueryWidget(p) {
    const n = p.n;
    const [searchTerm, setSearchTerm] = createSignal('');
    // On a connection/database switch the selection is cleared centrally; also clear
    // this widget's local search box so it doesn't keep filtering by a stale term.
    createEffect(on(resetTick, () => setSearchTerm(''), { defer: true }));
    // Widget-level search = [cols] | true → render a box and append the filter.
    const searchCols = () => (n.search?.columns?.length ? n.search.columns : (n.show || []));
    const wantSearch = !!n.search && (n.search.columns?.length || n.search.all);

    // Serialized, like the grid's: refetch only when a bind/term really changed.
    const depKey = createMemo(() => {
      const binds = {}; for (const path of n.query?.binds || []) binds[path] = bindValue(path);
      const terms = {}; for (const t of n.query?.search || []) terms[t] = scalarValue(t);
      return JSON.stringify({ binds, terms, st: searchTerm(), rt: refreshTick(), cid: connId(), db: database() });
    });
    const [data] = createResource(depKey, async (k) => {
      const key = JSON.parse(k);
      if (!n.query?.raw) return { columns: [], rows: [] };
      try {
        return await doQuery(n, key, {
          mode: n.search?.mode || 'contains', limit: n.kind === 'select' ? 200 : 500,
          searchCols: wantSearch ? searchCols() : undefined, searchTerm: wantSearch ? key.st : undefined,
        });
      } catch (e) { toast.error('Query failed: ' + (e?.message || e)); return { columns: [], rows: [] }; }
    });
    const cols = () => data()?.columns || [];
    // A memo: the row objects are stable per result set. A pick stores a COPY (see
    // pickRow) — the store MERGES a new object into the previous one, so storing a
    // listed row would let the next pick overwrite that row's data in the list.
    const rows = createMemo(() => (data()?.rows || []).map((r) => Object.fromEntries(cols().map((c, i) => [c, r[i]]))));
    const pickRow = (row) => setValues(n.name, row ? { ...row } : null);
    const showCols = () => (n.show && n.show.length ? n.show : cols());
    const dispCol = () => n.display || n.value || cols()[0];
    // Identity of a row across refetches: its value column, else its display column.
    const keyCol = () => n.value || dispCol();
    const rowKey = (row) => String(row?.[keyCol()] ?? '');
    const selKey = () => { const v = values[n.name]; return v != null && typeof v === 'object' ? rowKey(v) : null; };
    // Optional `column X { align, format }` display overlay on a table widget (n.show
    // still owns column selection/order — see tableColumnProps in validate.go). Same
    // align/format model as a grid column (GridWidget's alignStyle / fmtVal below).
    const colMeta = (name) => (n.children || []).find((c) => c.name === name);
    const colAlignStyle = (name) => {
      const a = colMeta(name)?.scalars?.align;
      return (a === 'right' || a === 'center' || a === 'left') ? { 'text-align': a } : undefined;
    };
    const colCellText = (name, val) => fmtVal(val, colMeta(name)?.scalars?.format);

    // If a re-query (typically a new search term) drops the currently-selected row out
    // of the result set, clear the now-orphaned selection: keeping a pick that's no
    // longer visible would leave dependent detail referencing an invisible row. The
    // selection is preserved whenever it's still present in the filtered list — and
    // refreshed from it (matched by the value column), so its other fields aren't
    // the stale ones.
    createEffect(() => {
      if (data.loading || !n.value) return;
      const sel = values[n.name];
      if (sel == null || typeof sel !== 'object') return;
      const fresh = rows().find((r) => String(r[n.value]) === String(sel[n.value]));
      if (!fresh) setValues(n.name, null);
      else if (JSON.stringify(fresh) !== JSON.stringify(sel)) pickRow(fresh);
    });

    // Searchable multi-column dropdown (Select2-style): a collapsed control showing the
    // selected value; clicking opens a popover with a search box + the rows as a table.
    if (n.kind === 'table' && n.scalars?.dropdown) {
      const [open, setOpen] = createSignal(false);
      // Close the popover when the connection/database changes (selection already cleared).
      createEffect(on(resetTick, () => setOpen(false), { defer: true }));
      const selRow = () => values[n.name];
      const selLabel = () => { const r = selRow(); return r ? String(r[dispCol()] ?? r[n.value] ?? '') : ''; };
      let rootEl;
      const onDocDown = (e) => { if (rootEl && !rootEl.contains(e.target)) setOpen(false); };
      onMount(() => document.addEventListener('mousedown', onDocDown));
      onCleanup(() => document.removeEventListener('mousedown', onDocDown));
      const pick = (row) => { pickRow(row); setOpen(false); setSearchTerm(''); };
      return (
        <div class="fdsl-field fdsl-combo" ref={rootEl}>
          <span class="fdsl-label">{n.label || n.name}</span>
          <button type="button" class="input fdsl-combo-btn" disabled={!p.enabled()}
                  onClick={() => setOpen((o) => !o)}>
            <span classList={{ muted: !selLabel() }}>{selLabel() || `— ${n.label || 'pilih'} —`}</span>
            <span class="fdsl-combo-caret">▾</span>
          </button>
          <Show when={open()}>
            <div class="fdsl-combo-pop">
              <input class="input fdsl-search" placeholder="Cari…" autofocus
                     value={searchTerm()} onInput={(e) => setSearchTerm(e.currentTarget.value)} />
              <div class="fdsl-grid fdsl-combo-grid">
                <table>
                  <thead><tr><For each={showCols()}>{(c) => <th style={colAlignStyle(c)}>{c}</th>}</For></tr></thead>
                  <tbody>
                    <Show when={!data.loading} fallback={<tr><td colspan={showCols().length} class="muted">Loading…</td></tr>}>
                      <For each={rows()}>{(row) => (
                        <tr class="fdsl-trow" classList={{ sel: n.value && row[n.value] != null && row[n.value] === scalarValue(n.name) }} onClick={() => pick(row)}>
                          <For each={showCols()}>{(c) => <td style={colAlignStyle(c)}>{colCellText(c, row[c])}</td>}</For>
                        </tr>
                      )}</For>
                      <Show when={rows().length === 0}><tr><td colspan={showCols().length} class="muted">no rows</td></tr></Show>
                    </Show>
                  </tbody>
                </table>
              </div>
            </div>
          </Show>
        </div>
      );
    }

    if (n.kind === 'select') {
      // Static-enum select: `options = [{value,label}] | ["a","b"]` with no query.
      // The plan carries these as n.options (plan.go optionList); drive the dropdown
      // from them and store the picked option's `value` — a primitive that
      // scalarValue passes straight through (so :name / {name} / `is set` all work).
      const staticOpts = !n.query?.raw ? (n.options || []) : null;
      return (
        <label class="fdsl-field">
          <span class="fdsl-label">{n.label || n.name}</span>
          <Show when={wantSearch}>
            <input class="input fdsl-search" placeholder="Cari…" value={searchTerm()} onInput={(e) => setSearchTerm(e.currentTarget.value)} />
          </Show>
          <Show
            when={staticOpts && staticOpts.length}
            fallback={
              // Controlled by row key: a refetch rebuilds the options, and an
              // uncontrolled select would fall back to the placeholder while the
              // stored pick lived on.
              <select class="input" disabled={!p.enabled()} onChange={(e) => {
                const i = e.currentTarget.selectedIndex - 1;
                pickRow(i >= 0 ? rows()[i] : null);
              }}>
                <option value="">{data.loading ? 'Loading…' : `— ${n.label || 'pilih'} —`}</option>
                <For each={rows()}>{(row) => (
                  <option selected={rowKey(row) === selKey()}>{String(row[dispCol()] ?? '')}</option>
                )}</For>
              </select>
            }
          >
            <select class="input" disabled={!p.enabled()} value={scalarValue(n.name) ?? ''}
                    onChange={(e) => setValues(n.name, e.currentTarget.value || null)}>
              <option value="">— {n.label || 'pilih'} —</option>
              <For each={staticOpts}>{(o) => <option value={o.value}>{o.label}</option>}</For>
            </select>
          </Show>
        </label>
      );
    }
    // table
    return (
      <div class="fdsl-tablewrap">
        <div class="fdsl-label">{n.label || n.name}</div>
        <Show when={wantSearch}>
          <input class="input fdsl-search" placeholder="Cari…" value={searchTerm()} onInput={(e) => setSearchTerm(e.currentTarget.value)} />
        </Show>
        <div class="fdsl-grid">
          <table>
            <thead><tr><For each={showCols()}>{(c) => <th style={colAlignStyle(c)}>{c}</th>}</For></tr></thead>
            <tbody>
              <Show when={!data.loading} fallback={<tr><td colspan={showCols().length} class="muted">Loading…</td></tr>}>
                <For each={rows()}>{(row) => (
                  <tr class="fdsl-trow" classList={{ sel: n.value && row[n.value] != null && row[n.value] === scalarValue(n.name) }} onClick={() => pickRow(row)}>
                    <For each={showCols()}>{(c) => <td style={colAlignStyle(c)}>{colCellText(c, row[c])}</td>}</For>
                  </tr>
                )}</For>
                <Show when={rows().length === 0}><tr><td colspan={showCols().length} class="muted">no rows</td></tr></Show>
              </Show>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function ButtonNode(p) {
    const n = p.n;
    const enabled = createMemo(() => evalProp(n, 'enabled', true));
    const [busy, setBusy] = createSignal(false);

    // `role = admin | [admin, subadmin]` — the button is shown only to a matching
    // role (saved-form access is still governed server-side by form grants).
    const roleOk = () => {
      const r = n.scalars?.role;
      if (r == null) return true;
      const allowed = (Array.isArray(r) ? r : [r]).map((x) => String(x).toLowerCase());
      return allowed.includes(String(userCtx().role || '').toLowerCase());
    };

    const onClick = async () => {
      if (!roleOk()) return;
      if (n.scalars?.confirm && !window.confirm(interp(n.scalars.confirm))) return;

      // Resolve the run{} tree into ordered server steps (call/exec/require, sent as
      // one transactional action) + client effects (toast/close/open). `if`/`when`
      // pick a branch; `for x in <array> { … }` iterates, binding `x` per item via
      // the `extra` scope (so `:x.Col` / `{x.Col}` resolve). The iterable is any
      // array in scope — a `sql.rows` let, or a grid accessor (g.rows/added/…).
      const serverSteps = [];
      const clientEffects = [];
      let unsupported = false;
      const resolveRun = (steps, extra) => {
        const sc = extra ? { ...scope(), ...extra } : scope();
        for (const st of steps || []) {
          if (st.op === 'if' || st.op === 'when') {
            if (safeEval(st.cond, sc)) resolveRun(st.then, extra);
            else if (st.op === 'if') resolveRun(st.else, extra);
          } else if (st.op === 'for') {
            const iter = safeEval(st.in, sc);
            if (Array.isArray(iter)) for (const item of iter) resolveRun(st.then, { ...(extra || {}), [st.var]: item });
          } else if (st.op === 'call' || st.op === 'exec' || st.op === 'require') {
            const binds = {};
            for (const path of st.binds || []) binds[path] = bindWith(path, extra);
            let set;
            if (st.set) { set = {}; for (const k in st.set) { const v = safeEval(st.set[k], sc); set[k] = v == null ? null : String(v); } }
            serverSteps.push({ op: st.op, sql: st.sql, binds, message: st.message, set });
          } else if (st.op === 'unsupported') {
            unsupported = true;
          } else {
            clientEffects.push({ st, extra });
          }
        }
      };
      resolveRun(n.run, null);
      if (unsupported) { toast.error('This action uses a step not supported yet.'); return; }

      // Enforce widget/column constraints (required/min/max/maxlen/pattern) + the
      // form-level validate{} before a mutating action — same opt-out as validate=false.
      if (serverSteps.length && n.scalars?.validate !== false) {
        const fe = firstFieldError();
        if (fe) { toast.error(fe); return; }
        const sc = scope();
        for (const v of props.plan?.validate || []) {
          if (v.cond !== undefined && v.cond !== null) {
            if (!safeEval(v.cond, sc)) { toast.error(v.message || 'Validation failed.'); return; }
          }
        }
        const sqlGuards = (props.plan?.validate || []).filter((v) => v.sql).map((v) => {
          const binds = {};
          for (const path of v.binds || []) binds[path] = bindValue(path);
          return { op: 'require', sql: v.sql, binds, message: v.message };
        });
        serverSteps.unshift(...sqlGuards);
      }

      setBusy(true);
      try {
        // Skip the DB round-trip when the run is purely client effects.
        if (serverSteps.length) {
          const res = await doAction(serverSteps, false);
          if (res.aborted) { toast.error(res.message || 'Action blocked.'); return; }
          setRefreshTick((t) => t + 1); // re-run queries after a write
        }
        // client effects, in order; `extra` carries any for-loop var for interpolation.
        for (const { st, extra } of clientEffects) {
          if (st.op === 'toast') {
            const lvl = st.level || 'success';
            if (lvl === 'alert') window.alert(interpWith(st.text, extra));
            else (toast[lvl] || toast.success)(interpWith(st.text, extra));
          } else if (st.op === 'close') {
            props.onClose?.();
          } else if (st.op === 'open') {
            if (props.onOpen) {
              const params = {};
              if (st.set) for (const k in st.set) { const v = safeEval(st.set[k], extra ? { ...scope(), ...extra } : scope()); params[k] = v == null ? null : v; }
              props.onOpen(st.target, params);
            } else {
              toast.info('“Open” works when running a saved form (not in the preview).');
            }
          }
        }
      } catch (e) {
        toast.error('Action failed: ' + (e?.message || e));
      } finally {
        setBusy(false);
      }
    };

    return <Show when={roleOk()}><button class="btn btn-primary fdsl-btn" disabled={!enabled() || busy()} onClick={onClick}>{busy() ? '…' : n.label}</button></Show>;
  }
}
